import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  CollectionEntryTreeValueModel,
  CollectionEntryValue,
  DataTypeEnum,
  FieldPresenterModel,
  FieldSeedModel,
  LocalizedSingularPluralModel,
  LocalizedValueModel,
  SingularPluralModel,
} from '@osos/shared/types';
import { Observable, combineLatest, of } from 'rxjs';
import { LanguageState } from '../../state/translation/language.state';

import { ComponentRegistryState } from '../../state';
import {
  mapCollectionItemValue,
  removeEmptyLocalesFromLocalized,
} from '../../utils';
import { TranslationDataService } from '../data-services/translation-data-service/translation-data.service';

@Injectable()
export class TypesUtilsService {
  private readonly _renderer: Renderer2;
  private _defaultLangKey: string;
  private _currentLanguage: string;
  private _supportedLocales: string[];
  constructor(
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
    private TranslationDataService: TranslationDataService,
  ) {
    this._renderer = rendererFactory.createRenderer(null, null);
    combineLatest([
      this.store.select(LanguageState.defaultLanguage),
      this.store.select(LanguageState.currentLanguage),
      this.store.select(LanguageState.supportedLocales),
    ]).subscribe(([defaultLang, currentLang, supportedLocales]) => {
      this._defaultLangKey = defaultLang?.languageName;
      this._currentLanguage = currentLang?.languageName;
      this._supportedLocales = supportedLocales;
    });
  }

  /**
   *  Returns a value converted to a string using the current language. *

   * */
  getLocaleString(
    value: LocalizedValueModel,
    placeholders?: Record<string, string>,
  ): string {
    if (typeof value !== 'object') {
      return value;
    }
    if (!Object.keys({ ...value }).length) {
      return '';
    }

    let localString =
      value[this._currentLanguage]?.trim() ||
      value[this._defaultLangKey]?.trim();

    if (!localString) {
      localString = Object.values(value).find((val) => !!val);
    }

    // Replace placeholders if provided
    if (placeholders && localString) {
      localString = this.replacePlaceholders(localString, placeholders);
    }

    return localString;
  }

  /**
   * Replaces placeholders in a message with actual values
   * Supports both {key} and {{key}} formats
   */
  replacePlaceholders(
    message: string,
    placeholders: Record<string, string>,
  ): string {
    if (!message || !placeholders) {
      return message;
    }

    return Object.entries(placeholders).reduce((result, [key, value]) => {
      // Replace both {key} and {{key}} patterns
      const pattern1 = new RegExp(`\\{${key}\\}`, 'g');
      const pattern2 = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      return result.replace(pattern1, value).replace(pattern2, value);
    }, message);
  }

  getDefaultLocaleString(value: LocalizedValueModel): string {
    if (!value) {
      return '';
    }
    return value[this._defaultLangKey];
  }

  getLocaleSingularPlural(
    value: SingularPluralModel,
  ): LocalizedSingularPluralModel {
    if (!value || !Object.keys(value)?.length) {
      return null;
    }
    return Object.keys(value).reduce((a, b) => {
      a[b] = this.getLocaleString(value[b]);
      return a;
    }, {});
  }

  // TODO: move this function and the other realted to tree nodes and collection tree to a separated class
  // These functions are utils functions, and aren't depnding on the services.
  parseCollectionEntryValueToTree(
    pItems: CollectionEntryValue[],
    lookupType: DataTypeEnum,
    locale_id: any,
    _userUiSettings: any,
  ): CollectionEntryTreeValueModel[] {
    const _map = {};
    const _roots: CollectionEntryTreeValueModel[] = [];
    const items: CollectionEntryTreeValueModel[] = pItems.map((item) => {
      return new CollectionEntryTreeValueModel({
        ...item,
        children: [],
        label: mapCollectionItemValue(
          item.value,
          lookupType,
          locale_id,
          _userUiSettings,
        ),
        data: item,
        relativeIndex: '',
        parent: item.parent,
      });
    });
    items.forEach((item, id) => {
      // TODO add model that extend from the lookup data model and add this props as additional property
      _map[item.id] = id;
    });
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < items.length; i++) {
      const element = items[i];
      if (element.parent) {
        element.relativeIndex =
          items[_map[element?.parent as string]].relativeIndex +
          '.' +
          (element.index + 1);
        items[_map[element?.parent as string]].children.push(element);
      } else {
        element.relativeIndex = (element.index + 1).toString();
        _roots.push(element);
      }
    }
    return [..._roots];
  }

  findTreeNodeWithinCollection(
    items: CollectionEntryTreeValueModel[],
    nodeId: string,
  ): CollectionEntryTreeValueModel {
    let stop = false;
    let foundNode;
    function innerFun(node: any): CollectionEntryTreeValueModel {
      if (node.id === nodeId) {
        stop = true;
        foundNode = node;
        return node;
      } else if (node.children?.length > 0 && !stop) {
        node.children.forEach((_child) => innerFun(_child));
      }
      return null;
    }
    items.forEach((_n) => {
      if (!stop) {
        innerFun(_n);
      }
    });
    return foundNode;
  }

  downloadBlob(blob: Blob, fileName: string): void {
    const downloadURL = URL.createObjectURL(blob);

    // Prepare the link of file:
    const link = this._renderer.createElement('a');
    this._renderer.setAttribute(link, 'href', downloadURL);
    this._renderer.setAttribute(link, 'download', fileName);
    this._renderer.appendChild(this.document.body, link);

    // Download the file:
    link.click();

    // Clear the added link:
    this._renderer.removeChild(this.document.body, link);
  }

  viewBlobContentInNewTab(blob: Blob): void {
    window.open(URL.createObjectURL(blob), '_blank');
  }

  generateFieldPresenterFromSeed(
    fieldSeed: FieldSeedModel,
  ): FieldPresenterModel {
    const componentModel = this.store.selectSnapshot(
      ComponentRegistryState.getComponent(
        fieldSeed.renderOptions?.editComponent,
      ),
    );

    if (!componentModel) {
      return null;
    }

    return {
      normalizedName: fieldSeed.normalizedName,
      dataType: fieldSeed.dataType ?? componentModel.componentType?.[0],
      name: fieldSeed.name,
      description: fieldSeed.description,
      renderOptions: {
        editComponent: {
          component: fieldSeed.renderOptions?.editComponent,
          options: {},
        },
        viewComponent: {
          component:
            fieldSeed.renderOptions?.viewComponent ??
            componentModel.preferredPairedComponents?.[0]?.name,
          options: {},
        },
      },
      validations: fieldSeed.validations,
      sourceOptions: null,
      options: {
        readonly: fieldSeed.options?.readonly,
      },
    } satisfies FieldPresenterModel;
  }

  /**
   * @description Filter the localized value object to only none empty supported languages locales
   * @param value
   * @returns
   */
  getNoneEmptyLocalesFrom(value: LocalizedValueModel): string[] {
    if (!value) return [];

    return Object.keys(value)
      .filter(
        (locale) =>
          this._supportedLocales.includes(locale) &&
          typeof value[locale] === 'string',
      )
      .filter((locale) => !!value[locale].trim());
  }

  // Count the value before call TranslationDataService once
  translateLocalizedToEnglish(value: LocalizedValueModel): Observable<string> {
    if (!value) return of(null);
    const fullLocalizedValue = removeEmptyLocalesFromLocalized(value);
    // If there is no value in localized, return null
    if (!fullLocalizedValue || !Object.keys(fullLocalizedValue))
      return of(null);
    // If there is english value, get the en work back
    if (value?.en) return of(value?.en);
    // if there is a value for the current language the user uses, use it as translation
    let localeValue: string;
    if (!!value[this._currentLanguage] && this._currentLanguage !== 'en') {
      localeValue = this._currentLanguage;
    } else {
      // if all of the previous conditions are not met, then get the first valid string
      localeValue = Object.keys(fullLocalizedValue).find(
        (key) => !!fullLocalizedValue[key],
      );
    }

    return this.TranslationDataService.translate(
      fullLocalizedValue[localeValue],
    );
  }
}
